import { captureException } from "@/plugins/sentry";

/**
 * カメラを開始します
 */
export async function startCamera(isFrontCamera = true) {
  return await new Promise<void>((resolve, reject) => {
    // @ts-ignore
    const cameraPreview = window.CameraPreview;
    if (cameraPreview === undefined) {
      reject();
      return;
    }
    cameraPreview.startCamera(
      {
        x: 0,
        y: 0,
        width: window.screen.width,
        height: window.screen.height,
        camera: isFrontCamera
          ? cameraPreview.CAMERA_DIRECTION.FRONT
          : cameraPreview.CAMERA_DIRECTION.BACK,
        // html 要素より裏側のレイヤーで起動する
        toBack: true,
        // プレビューをタップして写真を撮る
        tapPhoto: false,
        // プレビューをタップした箇所にフォーカスさせる (オートフォーカスに任せる)
        tapFocus: false,
        // プレビューをドラッグ可能にする
        previewDrag: false,
        // base64で返さずファイルパスを返す
        storeToFile: false
      },
      function() {
        resolve();
      },
      function(error: string) {
        captureException("failed: startCamera" + error);
        reject();
      }
    );
  });
}

/**
 * カメラで撮影します
 */
export async function takePicture(width?: number, height?: number) {
  return await new Promise<string>((resolve, reject) => {
    // @ts-ignore
    const cameraPreview = window.CameraPreview;
    if (cameraPreview === undefined) {
      reject();
      return;
    }
    cameraPreview.takePicture(
      {
        width: width ?? window.screen.width,
        height: height ?? window.screen.height,
        quality: 100
      },
      function(imgData: string) {
        const imgUrl = "data:image/jpeg;base64," + imgData;
        resolve(imgUrl);
      },
      function(error: string) {
        captureException("failed: takePicture" + error);
        reject();
      }
    );
  });
}

/**
 * カメラを停止します
 */
export async function stopCamera() {
  return await new Promise<void>((resolve, reject) => {
    // @ts-ignore
    const cameraPreview = window.CameraPreview;
    if (cameraPreview === undefined) {
      reject(false);
      return;
    }
    cameraPreview.stopCamera(
      function() {
        resolve();
      },
      function(error: string) {
        captureException("failed: stopCamera" + error);
        reject();
        return;
      }
    );
  });
}

/**
 * Crop a base64 string image given a cropping rectangle, in the same way that
 * the iOS cordova-plugin-camera-preview creates its preview image.
 * See captureOutput function src/ios/CameraRendererController.m of
 * https://github.com/cordova-plugin-camera-preview/cordova-plugin-camera-preview
 * for details on how the image preview is created.
 * Code adapted by github @youralien
 *
 * @param base64PictureData {String} Pure base64 string without contentType
 * @param rectWidth {Number} Preview rectangle width
 * @param rectHeight {Number} Preview rectangle height
 * @param callback {Function}
 * @see http://blog.mathocr.com/2017/06/09/camera-preview-with-cordova.html
 */
//
export function b64CropLikeCordova(
  base64PictureData: string,
  rectWidth: number,
  rectHeight: number,
  callback: Function
) {
  // image will contain ORIGINAL image
  const image = new Image();

  // image will contain CROPPED image
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // Load original image into image element
  image.src = base64PictureData;
  image.onload = function() {
    const scaleHeight = rectHeight / image.height;
    const scaleWidth = rectWidth / image.width;

    let scale;
    let xImgUnits;
    let yImgUnits;
    if (scaleHeight < scaleWidth) {
      scale = scaleWidth;
      xImgUnits = 0;
      yImgUnits = (image.height - rectHeight / scale) / 2;
    } else {
      scale = scaleHeight;
      xImgUnits = (image.width - rectWidth / scale) / 2;
      yImgUnits = 0;
    }

    const rectWidthImgUnits = rectWidth / scale;
    const rectHeightImgUnits = rectHeight / scale;

    // Set canvas size equivalent to interpolated rectangle size
    canvas.width = rectWidthImgUnits;
    canvas.height = rectHeightImgUnits;

    ctx!.drawImage(
      image,
      xImgUnits,
      yImgUnits, // Start CROPPING from (xImgUnits, yImgUnits).
      rectWidthImgUnits,
      rectHeightImgUnits, // Crop interpolated rectangle
      0,
      0, // Place the result at 0, 0 in the canvas,
      rectWidthImgUnits,
      rectHeightImgUnits
    ); // Crop interpolated rectangle

    // Get base64 representation of cropped image
    const croppedImgBase64 = canvas.toDataURL();

    // Now we are ready to send cropped image TO SERVER
    callback(croppedImgBase64);
  };
}
